
import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Json } from '@/integrations/supabase/types';

export interface WebsiteSettings {
  site_name?: string;
  site_description?: string;
  site_url?: string;
  admin_email?: string;
  logo_url?: string;
  favicon_url?: string;
  logo_mobile_height?: number;
  logo_desktop_height?: number;
  logo_max_width?: number;
  logo_max_width_desktop?: number;
  maintenance_mode?: boolean;
  allow_registration?: boolean;
  google_analytics_id?: string;
  meta_title?: string;
  meta_description?: string;
  meta_keywords?: string;
  native_ad_frequency?: number;
  footer_text?: string;
  discord_url?: string;
  twitter_url?: string;
  youtube_url?: string;
  github_url?: string;
  donate_url?: string;
  coffee_url?: string;
  donate_text?: string;
  coffee_text?: string;
  maintenance_mode_message?: string;
  auto_block_admin_access?: boolean;
  [key: string]: any;
}

export const useWebsiteSettings = () => {
  const queryClient = useQueryClient();

  const { data: settingsArray, isLoading, error, refetch } = useQuery({
    queryKey: ['website-settings'],
    queryFn: async () => {
      console.log('Fetching website settings from database...');
      
      const { data, error } = await supabase
        .from('website_settings')
        .select('key, value');
      
      if (error) {
        console.error('Database error fetching settings:', error);
        throw error;
      }
      
      console.log('Raw settings from database:', data);
      return data || [];
    },
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    retry: 2,
  });

  // Process settings with defaults
  const settings: WebsiteSettings = React.useMemo(() => {
    const defaultSettings: WebsiteSettings = {
      site_name: 'TerraGamers',
      site_description: 'Discover and play thousands of free online games. Action, puzzle, racing, and more!',
      site_url: '',
      admin_email: '',
      logo_url: '',
      favicon_url: '',
      logo_mobile_height: 48,
      logo_desktop_height: 64,
      logo_max_width: 200,
      logo_max_width_desktop: 280,
      maintenance_mode: false,
      allow_registration: false,
      google_analytics_id: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      native_ad_frequency: 8,
      footer_text: '',
      discord_url: '',
      twitter_url: '',
      youtube_url: '',
      github_url: '',
      donate_url: '',
      coffee_url: '',
      donate_text: '',
      coffee_text: '',
      maintenance_mode_message: "We are currently performing scheduled maintenance. Please check back soon.",
      auto_block_admin_access: true,
    };

    // If we're still loading or have no data, return defaults
    if (isLoading || !settingsArray) {
      console.log('Using defaults - still loading or no data:', { isLoading, settingsArray });
      return defaultSettings;
    }

    // Ensure settingsArray is an array
    if (!Array.isArray(settingsArray)) {
      console.warn('Settings data is not an array:', settingsArray);
      return defaultSettings;
    }

    console.log('Processing settings array:', settingsArray);

    // Convert array to object
    const processedSettings: Record<string, any> = {};
    
    settingsArray.forEach(setting => {
      if (!setting || !setting.key) {
        console.warn('Invalid setting found:', setting);
        return;
      }
      
      let value = setting.value;
      
      // Handle JSON parsing for complex values
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          value = parsed;
        } catch {
          // Keep as string if not valid JSON
        }
      }
      
      processedSettings[setting.key] = value;
    });

    console.log('Processed settings object:', processedSettings);

    // Helper function to safely convert to number
    const toNumber = (value: any, defaultValue: number): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsed = parseInt(value, 10);
        return isNaN(parsed) ? defaultValue : parsed;
      }
      return defaultValue;
    };

    // Helper function to safely convert to boolean
    const toBoolean = (value: any, defaultValue: boolean): boolean => {
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true';
      }
      if (typeof value === 'number') {
        return value !== 0;
      }
      return defaultValue;
    };

    // Merge with defaults, using database values when available
    const finalSettings: WebsiteSettings = {
      site_name: processedSettings.site_name || defaultSettings.site_name,
      site_description: processedSettings.site_description || defaultSettings.site_description,
      site_url: processedSettings.site_url || defaultSettings.site_url,
      admin_email: processedSettings.admin_email || defaultSettings.admin_email,
      logo_url: processedSettings.logo_url || defaultSettings.logo_url,
      favicon_url: processedSettings.favicon_url || defaultSettings.favicon_url,
      logo_mobile_height: toNumber(processedSettings.logo_mobile_height, defaultSettings.logo_mobile_height!),
      logo_desktop_height: toNumber(processedSettings.logo_desktop_height, defaultSettings.logo_desktop_height!),
      logo_max_width: toNumber(processedSettings.logo_max_width, defaultSettings.logo_max_width!),
      logo_max_width_desktop: toNumber(processedSettings.logo_max_width_desktop, defaultSettings.logo_max_width_desktop!),
      maintenance_mode: toBoolean(processedSettings.maintenance_mode, defaultSettings.maintenance_mode!),
      allow_registration: toBoolean(processedSettings.allow_registration, defaultSettings.allow_registration!),
      google_analytics_id: processedSettings.google_analytics_id || defaultSettings.google_analytics_id,
      meta_title: processedSettings.meta_title || defaultSettings.meta_title,
      meta_description: processedSettings.meta_description || defaultSettings.meta_description,
      meta_keywords: processedSettings.meta_keywords || defaultSettings.meta_keywords,
      native_ad_frequency: toNumber(processedSettings.native_ad_frequency, defaultSettings.native_ad_frequency!),
      footer_text: processedSettings.footer_text || defaultSettings.footer_text,
      discord_url: processedSettings.discord_url || defaultSettings.discord_url,
      twitter_url: processedSettings.twitter_url || defaultSettings.twitter_url,
      youtube_url: processedSettings.youtube_url || defaultSettings.youtube_url,
      github_url: processedSettings.github_url || defaultSettings.github_url,
      donate_url: processedSettings.donate_url || defaultSettings.donate_url,
      coffee_url: processedSettings.coffee_url || defaultSettings.coffee_url,
      donate_text: processedSettings.donate_text || defaultSettings.donate_text,
      coffee_text: processedSettings.coffee_text || defaultSettings.coffee_text,
      maintenance_mode_message: processedSettings.maintenance_mode_message || defaultSettings.maintenance_mode_message,
      auto_block_admin_access: toBoolean(processedSettings.auto_block_admin_access, defaultSettings.auto_block_admin_access!),
    };

    console.log('Final merged settings being returned:', finalSettings);
    return finalSettings;
  }, [settingsArray, isLoading]);

  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsToUpdate: Record<string, any>) => {
      console.log('Updating settings:', settingsToUpdate);
      
      const updatePromises = Object.entries(settingsToUpdate).map(async ([key, value]) => {
        const jsonValue: Json = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null
          ? value
          : JSON.stringify(value);

        const { error } = await supabase
          .from('website_settings')
          .upsert({ 
            key, 
            value: jsonValue, 
            description: `Setting for ${key}` 
          }, {
            onConflict: 'key'
          });
          
        if (error) {
          console.error(`Failed to update setting ${key}:`, error);
          throw error;
        }
        
        return { key, value };
      });

      const results = await Promise.all(updatePromises);
      console.log('Settings update results:', results);
      return results;
    },
    onSuccess: async () => {
      console.log('Settings updated successfully, invalidating queries...');
      await queryClient.invalidateQueries({ queryKey: ['website-settings'] });
      await queryClient.refetchQueries({ queryKey: ['website-settings'] });
      toast.success('Settings updated successfully');
    },
    onError: (error: any) => {
      console.error('Settings update error:', error);
      toast.error('Failed to update settings. Please try again.');
    },
  });

  return {
    settings,
    isLoading,
    error,
    refetch,
    updateSettings: updateSettingsMutation.mutate,
    isUpdating: updateSettingsMutation.isPending,
  };
};
